/**
 * HTTP utility functions
 * These are pure functions used by the HTTP client and middleware
 */

/**
 * Checks if a response is a Netlify timeout error (500 with text/plain body containing timeout message)
 * @param {import('axios').AxiosResponse} response - The axios response object
 * @returns {boolean} True if this appears to be a Netlify timeout error
 */
export const isNetlifyTimeout = (response) => {
    // Only check 500 status responses to avoid false positives
    if (response.status !== 500) {
        return false;
    }

    // Check content type - Netlify timeouts come as text/plain
    const contentType = response.headers?.['content-type'] || '';
    if (!contentType.includes('text/plain')) {
        return false;
    }

    // Check if response data is a string containing the Netlify timeout pattern
    if (typeof response.data !== 'string') {
        return false;
    }

    // Check for Netlify timeout error pattern
    // Matches "timeout", "timed out", "TimeoutError", etc.
    // Using word boundaries to avoid false positives like "setTimeout"
    return /\btimeout\b|\btimed out\b/i.test(response.data);
};

/**
 * Transforms Netlify timeout errors into user-friendly errors
 * @param {import('axios').AxiosError | Error} error - The axios error
 * @returns {Error} The transformed error or original error
 */
export const transformNetlifyTimeout = (error) => {
    // Handle non-object errors or errors without response
    if (
        !error ||
        typeof error !== 'object' ||
        !error.response ||
        !isNetlifyTimeout(error.response)
    ) {
        return error;
    }

    const originalStatus = error.response.status;

    const timeoutError = new Error(
        'Your request timed out. Refresh the page and try again or contact support@ghost.org',
    );
    timeoutError.name = 'HTTPError';
    timeoutError.code = 'NETLIFY_TIMEOUT';
    timeoutError.isNetlifyTimeout = true;
    // Create a shallow copy of the response to avoid mutating the original
    timeoutError.response = { ...error.response };
    // Transform the status code for axios compatibility
    timeoutError.response.status = 408; // Transform to 408 Request Timeout
    timeoutError.response.data = {
        error: {
            message: timeoutError.message,
            code: 'NETLIFY_TIMEOUT',
            originalError: error.response.data,
            context: {
                url: error.config?.url,
                original_status_code: originalStatus,
            },
        },
    };

    return timeoutError;
};

/**
 * Get the base URL for the application
 * @returns {string} The base URL
 */
export const getBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.origin;
    }
    // Fallback for test environments
    return '';
};

/**
 * Refresh JWT token and retry the original request
 * @param {object} params - Parameters for token refresh
 * @param {import('../utils/JWTokenHandler').default} params.tokenHandler - JWT token handler instance
 * @param {import('axios').InternalAxiosRequestConfig} params.originalRequest - The original request to retry
 * @param {import('axios').AxiosInstance} params.httpClient - HTTP client for retrying the request
 * @returns {Promise<import('axios').AxiosResponse>} The response from the retried request
 * @throws {Error} If token refresh fails
 */
export const refreshTokenAndRetry = async ({ tokenHandler, originalRequest, httpClient }) => {
    // Refresh the token
    const result = await tokenHandler.init();
    if (result?.error) {
        // JWTokenHandler already logs this error to Sentry
        throw result.error;
    }

    // Update request with new JWT
    const existingData = originalRequest.data ? JSON.parse(originalRequest.data) : {};
    originalRequest.data = JSON.stringify({
        jwt: tokenHandler.jwt,
        data: existingData.data ?? {},
        options: existingData.options ?? {},
    });

    // Axios' Node adapter retains the original body's length on retries.
    // Let it recalculate after replacing the JWT, which can have a new length.
    delete originalRequest.headers?.['Content-Length'];

    // Retry the request
    return httpClient(originalRequest);
};
